import React, { useState } from "react";
import { useFetch, api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRightCircle, CheckCircle2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, StatusChip, ConfidenceBadge, SeverityChip, LoadingState, OutcomeChip } from "@/components/app/primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const gapStatusTone = (s) => s === "Confirmed" ? "validated" : s === "Leadership decision required" ? "risk" : s === "Data missing" ? "danger" : "ai";

// ---- Live engagement: Data Quality & Validation Log (never blends with demo) ----

const DQ_CATEGORY_META = {
  confirmed_issue: { label: "Confirmed data issue", tone: "danger" },
  mapping_assumption: { label: "Mapping assumption", tone: "ai" },
  data_gap: { label: "Data gap", tone: "risk" },
  definition_conflict: { label: "Definition conflict", tone: "risk" },
  validation_required: { label: "Validation required", tone: "risk" },
  resolved: { label: "Resolved", tone: "validated" },
};

const DataQualityLog = ({ items, refetch, roleKey }) => {
  const [busy, setBusy] = useState(null);
  const setStatus = async (item, status) => {
    try {
      setBusy(item.id);
      await api.patch(`/live/dq-log/${item.id}`, { status, actor_role: roleKey });
      toast.success(status === "resolved" ? "Item marked resolved" : `Item moved to ${status.replace("_", " ")}`);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };
  const open = items.filter((i) => i.status !== "resolved");
  const resolved = items.filter((i) => i.status === "resolved");
  return (
    <div className="space-y-3" data-testid="dq-log-section">
      <p className="text-[11px] text-[--tx-3] leading-relaxed max-w-3xl">
        Auto-derived from the live data pipeline - profiling exceptions, mapping assumptions, ingestion statistics,
        missing datasets, and definition status. No mapping ambiguity is silently hidden. Items reopen automatically
        if their trigger recurs; resolved items stay resolved.
      </p>
      {items.length === 0 && <div className="text-center text-sm text-[--tx-3] py-8" data-testid="dq-log-empty">No data-quality items - upload and map files in Live mode to populate this log.</div>}
      <div className="space-y-2" data-testid="dq-log-open-list">
        {open.map((i) => {
          const meta = DQ_CATEGORY_META[i.category] || { label: i.category, tone: "ai" };
          return (
            <div key={i.id} className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5" data-testid={`dq-item-${i.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <StatusChip tone={meta.tone}>{meta.label}</StatusChip>
                <StatusChip tone={i.severity === "high" ? "danger" : "neutral"}>{i.severity}</StatusChip>
                {i.status === "in_review" && <StatusChip tone="ai">In review</StatusChip>}
                <span className="ml-auto text-[10px] font-mono text-[--tx-3]">{(i.updated_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <h4 className="text-[13px] font-semibold text-[--tx-1] leading-snug mb-1">{i.title}</h4>
              <p className="text-[11px] text-[--tx-2] leading-relaxed mb-2">{i.detail}</p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-[--tx-3] truncate">source: {i.source}</span>
                <div className="ml-auto flex gap-1.5">
                  {i.status !== "in_review" && (
                    <button onClick={() => setStatus(i, "in_review")} disabled={busy === i.id} data-testid={`dq-review-${i.id}`}
                      className="rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1 text-[10px] font-semibold text-[--tx-2] hover:text-[--tx-1] disabled:opacity-40 transition-colors">
                      Mark in review
                    </button>
                  )}
                  <button onClick={() => setStatus(i, "resolved")} disabled={busy === i.id} data-testid={`dq-resolve-${i.id}`}
                    className="rounded-lg border border-[--green] bg-transparent px-2.5 py-1 text-[10px] font-semibold text-[--green] hover:bg-[--green-soft] disabled:opacity-40 transition-colors">
                    Resolve
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {resolved.length > 0 && (
        <div data-testid="dq-log-resolved-list">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1.5">Resolved ({resolved.length})</div>
          <div className="space-y-1.5">
            {resolved.map((i) => (
              <div key={i.id} className="rounded-lg border border-[--hairline] bg-[--panel-dark] px-3 py-2 opacity-60 flex items-center gap-2">
                <CheckCircle2 size={12} className="text-[--green] shrink-0" />
                <span className="text-[11px] text-[--tx-2] truncate">{i.title}</span>
                <span className="ml-auto text-[10px] font-mono text-[--tx-3] shrink-0">{(i.updated_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ControlTowerPage() {
  const { data: gaps, loading: lg, refetch: refetchGaps } = useFetch("/gaps");
  const { data: risks, loading: lr } = useFetch("/risks");
  const { data: issues, loading: li } = useFetch("/issues");
  const { data: actions, refetch: refetchActions } = useFetch("/actions");
  const { refreshOverview, mode, roleKey } = useApp();
  const { data: dqLog, refetch: refetchDq } = useFetch(mode === "live" ? "/live/dq-log" : null);
  const [selectedGap, setSelectedGap] = useState(null);
  const [converting, setConverting] = useState(false);

  if (lg || lr || li) return <LoadingState message="Loading registers..." />;

  const convert = async (gap) => {
    setConverting(true);
    try {
      const res = await api.post(`/gaps/${gap.id}/convert`, {});
      if (res.data.already_converted) {
        toast.info(`Already converted - ${res.data.action.id} exists`);
      } else {
        toast.success(`Gap converted to action ${res.data.action.id}`, { description: `${res.data.action.title} · Owner: ${res.data.action.owner_role} · Due: ${res.data.action.due}` });
      }
      refetchGaps();
      refetchActions();
      refreshOverview();
      setSelectedGap(null);
    } catch (e) {
      toast.error("Conversion failed");
    } finally {
      setConverting(false);
    }
  };

  return (
    <div data-testid="control-tower-page">
      <PageHeader
        kicker="Scene 6 - Gap-to-action conversion"
        title="Gap, Risk & Issue Control Tower"
        subtitle="Risks are potential future impact. Issues are impact happening now. Gaps are missing capability, evidence, ownership, or behavior. They are managed differently - so they are separated."
      />

      <Tabs defaultValue="gaps" data-testid="control-tower-tabs">
        <TabsList className="bg-[--shell-2] border border-[--hairline] mb-4">
          <TabsTrigger value="gaps" data-testid="tab-gaps" className="text-xs data-[state=active]:bg-[--six-crimson-soft] data-[state=active]:text-[--tx-1]">Gaps ({gaps?.length})</TabsTrigger>
          <TabsTrigger value="risks" data-testid="tab-risks" className="text-xs data-[state=active]:bg-[--six-crimson-soft] data-[state=active]:text-[--tx-1]">Risks ({risks?.length})</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues" className="text-xs data-[state=active]:bg-[--six-crimson-soft] data-[state=active]:text-[--tx-1]">Issues ({issues?.length})</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions" className="text-xs data-[state=active]:bg-[--six-crimson-soft] data-[state=active]:text-[--tx-1]">Action log ({actions?.length})</TabsTrigger>
          {mode === "live" && (
            <TabsTrigger value="dq" data-testid="tab-dq-log" className="text-xs data-[state=active]:bg-[--six-crimson-soft] data-[state=active]:text-[--tx-1]">
              Data quality log ({(dqLog || []).filter((d) => d.status !== "resolved").length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="gaps">
          <div className="grid md:grid-cols-2 gap-3" data-testid="gap-register">
            {(gaps || []).map((gap) => (
              <div key={gap.id} onClick={() => setSelectedGap(gap)} data-testid={`gap-card-${gap.id}`}
                className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5 cursor-pointer hover:border-[--heat-3] transition-colors">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="font-mono text-[10px] text-[--tx-3]">{gap.id}</span>
                  <StatusChip tone={gapStatusTone(gap.status)}>{gap.status}</StatusChip>
                  <SeverityChip severity={gap.severity} />
                  <span className="font-mono text-[10px] text-[#E86A6A] ml-auto">{gap.rcc}</span>
                </div>
                <h4 className="text-[13px] font-semibold text-[--tx-1] leading-snug mb-1">{gap.title}</h4>
                <p className="text-xs text-[--tx-3] leading-snug line-clamp-2 mb-2">{gap.description}</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-[--tx-3]">Owner: <span className="text-[--tx-2]">{gap.owner_needed}</span></span>
                  {gap.converted_action_id ? (
                    <StatusChip tone="validated"><CheckCircle2 size={10} className="mr-0.5" /> {gap.converted_action_id}</StatusChip>
                  ) : (
                    <span className="text-[10px] text-[--ai] font-medium">Click to convert →</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="risks">
          <div className="grid md:grid-cols-2 gap-3" data-testid="risk-register">
            {(risks || []).map((r) => (
              <div key={r.id} className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5" data-testid={`risk-card-${r.id}`}>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="font-mono text-[10px] text-[--tx-3]">{r.id}</span>
                  <StatusChip tone={r.type === "Known" ? "neutral" : r.type === "Unknown" ? "ai" : "risk"}>{r.type} risk</StatusChip>
                  <StatusChip tone={r.probability === "High" ? "danger" : "neutral"}>P: {r.probability}</StatusChip>
                  <StatusChip tone={r.impact === "High" ? "danger" : "neutral"}>I: {r.impact}</StatusChip>
                  <StatusChip tone={r.status === "Mitigating" ? "validated" : r.status === "Monitoring" ? "ai" : "risk"} className="ml-auto">{r.status}</StatusChip>
                </div>
                <h4 className="text-[13px] font-semibold text-[--tx-1] leading-snug mb-1">{r.title}</h4>
                <p className="text-xs text-[--tx-3] leading-snug mb-2.5">{r.description}</p>
                <div className="grid grid-cols-1 gap-1.5 text-[11px]">
                  <div className="flex gap-2"><span className="text-[--amber] shrink-0 w-20 font-mono text-[10px] uppercase pt-px">Indicators</span><span className="text-[--tx-2]">{r.indicators}</span></div>
                  <div className="flex gap-2"><span className="text-[--amber] shrink-0 w-20 font-mono text-[10px] uppercase pt-px">Triggers</span><span className="text-[--tx-2]">{r.triggers}</span></div>
                  <div className="flex gap-2"><span className="text-[--green] shrink-0 w-20 font-mono text-[10px] uppercase pt-px">Mitigation</span><span className="text-[--tx-2]">{r.mitigation}</span></div>
                  <div className="flex gap-2"><span className="text-[--ai] shrink-0 w-20 font-mono text-[10px] uppercase pt-px">Contingency</span><span className="text-[--tx-2]">{r.contingency}</span></div>
                </div>
                <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-[--divider]">
                  <span className="text-[10px] text-[--tx-3]">Owner: <span className="text-[--tx-2]">{r.owner_role}</span></span>
                  <OutcomeChip outcome={r.six_outcome} />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="issues">
          <div className="space-y-3" data-testid="issue-register">
            {(issues || []).map((iss) => (
              <div key={iss.id} className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5" data-testid={`issue-card-${iss.id}`}>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="font-mono text-[10px] text-[--tx-3]">{iss.id}</span>
                  <StatusChip tone="danger">Live now</StatusChip>
                  <SeverityChip severity={iss.severity} />
                  <StatusChip tone={iss.status === "Escalated" ? "risk" : iss.status === "In progress" ? "ai" : "neutral"} className="ml-auto">{iss.status}</StatusChip>
                </div>
                <h4 className="text-[13px] font-semibold text-[--tx-1] leading-snug mb-1">{iss.title}</h4>
                <p className="text-xs text-[--tx-3] leading-snug mb-2">{iss.description}</p>
                <div className="grid md:grid-cols-3 gap-2 text-[11px]">
                  <div><span className="text-[--tx-3]">Impact now: </span><span className="text-[--tx-2]">{iss.impact_now}</span></div>
                  <div><span className="text-[--tx-3]">Action: </span><span className="text-[--tx-2]">{iss.action}</span></div>
                  <div><span className="text-[--tx-3]">Owner: </span><span className="text-[--tx-2]">{iss.owner_role}</span> · <span className="font-mono text-[10px] text-[--ai]">{iss.related_gap}</span></div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="actions">
          <div className="space-y-3" data-testid="action-log">
            {(actions || []).map((a) => (
              <div key={a.id} className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5" data-testid={`action-card-${a.id}`}>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="font-mono text-[10px] text-[--tx-3]">{a.id}</span>
                  <StatusChip tone={a.status === "Open" ? "ai" : a.status === "Decision pending" ? "risk" : "validated"}>{a.status}</StatusChip>
                  {a.source_gap_id && <span className="font-mono text-[10px] text-[--ai]">from {a.source_gap_id}</span>}
                  <span className="ml-auto text-[11px] font-mono text-[--amber]">Due: {a.due}</span>
                </div>
                <h4 className="text-[13px] font-semibold text-[--tx-1] leading-snug mb-1.5">{a.title}</h4>
                <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                  <div><span className="text-[--tx-3]">Owner role: </span><span className="text-[--tx-2]">{a.owner_role}</span></div>
                  <div><span className="text-[--tx-3]">Decision needed: </span><span className="text-[--tx-2]">{a.decision_needed}</span></div>
                  <div><span className="text-[--tx-3]">Expected impact: </span><span className="text-[--tx-2]">{a.expected_impact}</span></div>
                  <div><span className="text-[--tx-3]">Measurement: </span><span className="text-[--tx-2]">{a.measurement}</span></div>
                </div>
              </div>
            ))}
            {(actions || []).length === 0 && <div className="text-center text-sm text-[--tx-3] py-8">No actions yet - convert a gap to create one.</div>}
          </div>
        </TabsContent>

        {mode === "live" && (
          <TabsContent value="dq">
            <DataQualityLog items={dqLog || []} refetch={refetchDq} roleKey={roleKey} />
          </TabsContent>
        )}
      </Tabs>

      <Sheet open={!!selectedGap} onOpenChange={(o) => !o && setSelectedGap(null)}>
        <SheetContent className="bg-[--shell-2] border-[--hairline] w-full sm:max-w-md overflow-y-auto thin-scroll" data-testid="gap-detail-drawer">
          {selectedGap && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[--tx-1] font-display flex items-center gap-2">
                  <span className="font-mono text-xs text-[--tx-3]">{selectedGap.id}</span> {selectedGap.title}
                </SheetTitle>
                <SheetDescription className="text-[--tx-3] text-xs">{selectedGap.dimension} · {selectedGap.rcc}</SheetDescription>
              </SheetHeader>
              <div className="space-y-3.5 mt-4">
                <div className="flex flex-wrap gap-2">
                  <StatusChip tone={gapStatusTone(selectedGap.status)}>{selectedGap.status}</StatusChip>
                  <SeverityChip severity={selectedGap.severity} />
                  <ConfidenceBadge level={selectedGap.confidence} />
                  <OutcomeChip outcome={selectedGap.six_outcome} />
                </div>
                {[
                  ["Description", selectedGap.description], ["Evidence", selectedGap.evidence], ["Impact pathway", selectedGap.impact_pathway],
                  ["Data needed", selectedGap.data_needed], ["Interview needed", selectedGap.interview_needed],
                  ["Recommended action", selectedGap.recommended_action], ["Risk if ignored", selectedGap.risk_if_ignored], ["Measurement approach", selectedGap.measurement],
                ].map(([l, v]) => (
                  <div key={l}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-0.5">{l}</div>
                    <p className="text-[13px] text-[--tx-2] leading-relaxed">{v}</p>
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-[--tx-3] mb-0.5">Owner needed</div><div className="text-[--tx-1] font-medium">{selectedGap.owner_needed}</div></div>
                  <div><div className="text-[--tx-3] mb-0.5">Timing</div><div className="text-[--tx-1] font-medium">{selectedGap.timing}</div></div>
                </div>
                {selectedGap.dependencies?.length > 0 && (
                  <div><div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Dependencies</div>
                    <div className="flex flex-wrap gap-1.5">{selectedGap.dependencies.map((d) => <StatusChip key={d} tone="risk">{d}</StatusChip>)}</div></div>
                )}
                <div className="pt-2 sticky bottom-0 bg-[--shell-2] pb-1">
                  {selectedGap.converted_action_id ? (
                    <div className="rounded-lg border border-[--green] bg-[--green-soft] px-3 py-2.5 flex items-center gap-2">
                      <CheckCircle2 size={15} className="text-[--green]" />
                      <span className="text-xs font-medium text-[--tx-1]">Converted to {selectedGap.converted_action_id} - tracked in Action log</span>
                    </div>
                  ) : (
                    <button onClick={() => convert(selectedGap)} disabled={converting} data-testid="convert-gap-to-action-button"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50">
                      <ArrowRightCircle size={15} /> {converting ? "Converting..." : "Convert Gap to Action"}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
